import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import {
  ChooseSelectorStep,
  ChooseDataByPartitionsStep,
  ChooseDataByListStep,
  ChooseDataByExecutionStep,
  ConfirmExecutionStep,
} from '../components';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  button: {
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

function StepPanel(props) {
  const { children, value, index, condition, ...other } = props;

  return (
    <div
      hidden={value !== index || !condition}
      id={`full-width-steppanel-${index}`}
      aria-labelledby={`full-width-step-${index}`}
      {...other}
    >
      {value === index && (
        <Box ml={0} mt={1}>{children}</Box>
      )}
    </div>
  );
}

StepPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
  condition: PropTypes.bool
};

StepPanel.defaultProps = {
  condition: true,
};

function getWorkloadTargetFolder(workload) {
  try {
    return workload.DataConfiguration.TargetDataset.Location.Prefix.replace(/\/$/, "").split('/').reverse()[0];
  } catch(e) {
    return "";
  }
}

function getWorkloadImage(workload) {
  try {
    return workload.WorkloadConfiguration.ImageTag;
  } catch(e) {
    return "";
  }
}

function getWorkloadMemory(workload) {
  try {
    return workload.WorkloadConfiguration.ContainerMemory;
  } catch(e) {
    return "";
  }
}

function fmtWorkloadDefaults(resource) {
  return {
    targetFolder: getWorkloadTargetFolder(resource),
    image: getWorkloadImage(resource),
    memory: getWorkloadMemory(resource),
  };
}

//
// this formats the request payload sent to the AppSync
// startExecution mutation method to initiate the execution
//
function fmtRequestPayload(data) {

  const workloadDefaults=fmtWorkloadDefaults(data.resource);
  let selector = {};
  if(data.selectorType==='partitions') {
    selector['Dataset']=data.resource.DataConfiguration.SourceDatasets[data.selectedSourceDataset].ResourceId;
    if(data.selectedPrefix && data.selectedPrefix.trim().length>0) {
      selector['Prefix']=data.selectedPrefix.trim();
    }
    if(
      (data.selectedStartPartition && data.selectedStartPartition.trim().length>0) ||
      (data.selectedUntilPartition && data.selectedUntilPartition.trim().length>0) ) {
      const partition={};
      if(data.selectedStartPartition && data.selectedStartPartition.trim().length>0) {
        partition['Start']=data.selectedStartPartition.trim();
      } 
      if(data.selectedUntilPartition && data.selectedUntilPartition.trim().length>0) {
        partition['Until']=data.selectedUntilPartition.trim();
      }
      selector['Partition']=partition;
    }
    if(data.limit>0) {
      selector['Limit']=data.limit;
    }
  
  } else if(data.selectorType==='list') {
    const rows=data.selectedObjects.split('\n').map(r=>r.trim()).filter(r=>r.startsWith("s3://"));
    selector=(rows.length===1)?rows[0]:rows;
  } else if(data.selectorType==='execution') {
    selector=data.executionInputSelectors.length===1?
      data.executionInputSelectors[0]:data.executionInputSelectors;
  }

  const overrides = {};
  if(data.image.length && data.image !== workloadDefaults.image ) {
    overrides['Image']=data.image;
  }
  if(data.memory.length && data.memory !== workloadDefaults.memory ) {
    overrides['Memory']=data.memory;
  }
  if(data.targetFolder.length && data.targetFolder !== workloadDefaults.targetFolder ) {
    overrides['TargetFolder']=data.targetFolder;
  }
  if(data.isDryrun) {
    overrides['Dryrun']=true;
  }

  return {
    "Description": data.description,
    "Overrides": overrides,
    "Inputs": selector
  };
}

export default function StartExecutionDialog(props) {
  const { resource, open, onCancel, onExecute } = props;
  const classes = useStyles();

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());

  const [description, setDescription] = React.useState("");

  const [selectorType, setSelectorType] = React.useState('partitions');
  const [selectedSourceDataset, setSelectedSourceDataset] = React.useState(0);
  const [selectedPrefix, setSelectedPrefix] = React.useState('');
  const [selectedStartPartition, setSelectedStartPartition] = React.useState('');
  const [selectedUntilPartition, setSelectedUntilPartition] = React.useState('');

  const [selectedObjects, setSelectedObjects] = React.useState("");

  const [executionInputSelectors, setExecutionInputSelectors] = React.useState([]);

  const [image, setImage] = React.useState("");
  const [memory, setMemory] = React.useState("");
  const [targetFolder, setTargetFolder] = React.useState("");
  const [limit, setLimit] = React.useState(0);
  const [isDryrun, setIsDryrun] = React.useState(false);

  const [ selectedObjectsOk, setSelectedObjectsOk ] = React.useState(false);

  
  const [request, setRequest] = React.useState(fmtRequestPayload({
      resource: resource,
      selectorType: selectorType,
      selectedSourceDataset: selectedSourceDataset,
      selectedPrefix: selectedPrefix,
      selectedStartPartition: selectedStartPartition,
      selectedUntilPartition: selectedUntilPartition,
      selectedObjects: selectedObjects,
      executionInputSelectors: executionInputSelectors,
      image: image,
      memory: memory,
      targetFolder: targetFolder,
      description: description,
      limit: limit,
      isDryrun: isDryrun,
  }));
  
  //
  // stepper handlers
  //
  const steps = ['Choose selector', 'Choose data', 'Start workload'];

  const isStepOptional = (step) => {
    return step === -1;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
       throw new Error("You can't skip a step that isn't optional.");
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  //
  // form handlers
  // 
  const handleSelectorTypeChange = (type) => {
    setSelectorType(type);
  };

  const handleDatasetChange = (index) => {
    setSelectedSourceDataset(index);
  };
  const handlePrefixChange = (prefix) => {
    setSelectedPrefix(prefix);
  };
  const handleStartPartitionChange = (partition) => {
    setSelectedStartPartition(partition);
  };
  const handleUntilPartitionChange = (partition) => {
    setSelectedUntilPartition(partition);
  };

  const handleSelectedObjectsChange = (list) => {
    setSelectedObjects(list);
  };
  const handleSetSelectedObjectsOk = (value) => {
    setSelectedObjectsOk(value);
  };
  
  const handleExecutionInputSelectorsChange = (selectors) => {
    setExecutionInputSelectors(selectors);
    console.log("SELECTORS: ",selectors)
  };

  const handleImageChanged = (image) => {
    setImage(image);
  };
  const handleMemoryChanged = (memory) => {
    setMemory(memory);
  };
  const handleTargetFolderChanged = (folder) => {
    setTargetFolder(folder);
  };
  const handleDescriptionChanged = (desc) => {
    setDescription(desc);
  };
  const handleLimitChanged = (limit) => {
    setLimit(limit);
  };
  const handleDryrunChanged = (enabled) => {
    setIsDryrun(enabled);
  };
  
  const handleExecute = () => {
    if(onExecute) {
      onExecute(request);
    }
  };
  const handleCancel = () => {
    if(onCancel) {
      onCancel();
    }
  };
  
  React.useEffect(() => {
    setRequest(fmtRequestPayload({
      resource: resource,
      selectorType: selectorType,
      selectedSourceDataset: selectedSourceDataset,
      selectedPrefix: selectedPrefix,
      selectedStartPartition: selectedStartPartition,
      selectedUntilPartition: selectedUntilPartition,
      selectedObjects: selectedObjects,
      executionInputSelectors: executionInputSelectors,
      image: image,
      memory: memory,
      targetFolder: targetFolder,
      description: description,
      limit: limit,
      isDryrun: isDryrun,
    }));
  }, [
    resource,
    selectorType, 
    selectedSourceDataset,
    selectedPrefix,
    selectedStartPartition,
    selectedUntilPartition,
    selectedObjects,
    executionInputSelectors,
    image,
    memory,
    targetFolder,
    description,
    limit,
    isDryrun,
  ]);

  return (
    <Dialog open={open} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Start a Workload Execution</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} variant="outlined">
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            if (isStepOptional(index)) {
              labelProps.optional = <Typography variant="caption">Optional</Typography>;
            }
            if (isStepSkipped(index)) {
              stepProps.completed = false;
            }
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <div style={{ minHeight: "250px", marginTop: "32px" }} >
          <StepPanel value={activeStep} index={0}>
            <ChooseSelectorStep 
              selectorType={selectorType}
              onSelectorTypeChange={handleSelectorTypeChange}/>
          </StepPanel>
          <StepPanel value={activeStep} index={1} condition={selectorType==='partitions'}>
            <ChooseDataByPartitionsStep 
              datasets={resource.DataConfiguration.SourceDatasets}
              selectedDataset={selectedSourceDataset}
              selectedPrefix={selectedPrefix}
              selectedStartPartition={selectedStartPartition}
              selectedUntilPartition={selectedUntilPartition}
              onPrefixChange={handlePrefixChange}
              onStartPartitionChange={handleStartPartitionChange}
              onUntilPartitionChange={handleUntilPartitionChange}
              onDatasetChange={handleDatasetChange}
            />
          </StepPanel>
          <StepPanel value={activeStep} index={1} condition={selectorType==='list'}>
            <ChooseDataByListStep 
              datasets={resource.DataConfiguration.SourceDatasets}
              selectedObjects={selectedObjects}
              selectedObjectsOk={selectedObjectsOk}
              onDescriptionChange={handleDescriptionChanged}
              onSelectedObjectsChange={handleSelectedObjectsChange}
              onSelectedObjectsOk={handleSetSelectedObjectsOk}
            />
          </StepPanel>
          <StepPanel value={activeStep} index={1} condition={selectorType==='execution'}>
            <ChooseDataByExecutionStep 
              workloadId={resource.ResourceId}
              inputSelectors={executionInputSelectors}
              onInputSelectorsChange={handleExecutionInputSelectorsChange}
            />
          </StepPanel>
          <StepPanel value={activeStep} index={2}>
            <ConfirmExecutionStep
              defaults={fmtWorkloadDefaults(resource)}
              request={request}
              onImageChanged={handleImageChanged}
              onMemoryChanged={handleMemoryChanged}
              onTargetFolderChanged={handleTargetFolderChanged}
              onDescriptionChanged={handleDescriptionChanged}
              onLimitChanged={handleLimitChanged}
              onDryrunChanged={handleDryrunChanged}
            />
          </StepPanel>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={handleCancel} className={classes.button}>
            Cancel
          </Button>
          <Button disabled={activeStep === 0} onClick={handleBack} className={classes.button}>
            Back
          </Button>
          {isStepOptional(activeStep) && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSkip}
              className={classes.button}
            >
              Skip
            </Button>
          )}

          <Button
            disabled={activeStep > 0 && ( 
              (!selectedObjectsOk && selectorType==='list') || 
              (!executionInputSelectors.length && selectorType==='execution')
            )}
            variant="contained"
            color="primary"
            onClick={activeStep === steps.length - 1 ? handleExecute : handleNext}
            className={classes.button}
          >
            {activeStep === steps.length - 1 ? 'Execute' : 'Next'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

StartExecutionDialog.propTypes = {
  resource: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func, 
  onExecute: PropTypes.func,
};
